<template>
    <section id="ally_spotlight" style="padding-bottom: 0px; padding-top: 2rem;">
        <div class="container">
            <div class="row pb-5">
                <div class="col-12">
                    <router-link to="/ally-interest">
                        <h2 class="text-coral">{{ $t("allies_interest") }}</h2>
                    </router-link>
                </div>
            </div>

            <div class="row">
                <div class="col-lg-12">
                    <swiper class="interest-allies-swiper" ref="sliderContainer" :slides-per-view="defaultSlidesPerView"
                        :slides-per-group="effectiveSlidesPerGroup" :space-between="defaultSpaceBetween"
                        :pagination="{ clickable: true }" :breakpoints="sliderBreakpoints"
                        @swiper="handleMainSwiperReady" :loop="shouldLoop" :navigation="true"
                        :autoplay="{ delay: 10000, disableOnInteraction: false }"
                        :keyboard="{ enabled: false }"
                        :a11y="{ enabled: false }">
                        <swiper-slide v-for="(allyInfo, index) in items" :key="index">
                            <div class="theme_common_box_two img_hover" @click="handleCardClick(allyInfo, index)"
                                :data-ally-id="String(allyInfo.id)" :data-slider-index="index">
                                <div class="theme_two_box_img">
                                    <div v-if="hasDiscount(allyInfo.discount_percentage)" class="discount-badge">
                                        {{ formatDiscount(allyInfo.discount_percentage) }}
                                    </div>
                                    <img :src="allyInfo.images?.[0]?.path || '/default-image.jpg'" alt="img"
                                        @click.stop="handleImageClick(allyInfo, index)" />
                                </div>
                                <div class="theme_two_box_content">
                                    <h4>{{ allyInfo.name }}</h4>
                                    <h3 class="text-coral">{{ allyInfo.sub_category?.name }}</h3>
                                </div>
                            </div>
                        </swiper-slide>
                    </swiper>

                    <div class="d-flex justify-content-end">
                        <router-link to="/ally-interest" class="text-coral font-weight-bold">
                            {{ $t("view_more_allies") }}
                        </router-link>
                    </div>
                </div>

                <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
                    <div class="modal-content" ref="modalContent">
                        <div class="modal-header">
                            <button @click="closeModal" class="close-btn">&times;</button>
                        </div>
                        <div class="modal-body">
                            <div v-if="selectedItem">
                                <div class="modal-slider">
                                    <swiper :loop="true" :navigation="true"
                                        :autoplay="{ delay: 3000, disableOnInteraction: false }" class="swiper-modal">
                                        <swiper-slide v-for="(image, index) in selectedItem.images || []" :key="index">
                                            <img :src="image.path"
                                                :alt="selectedItem.category?.name || selectedItem.name"
                                                @load="handleModalImageLoad($event, image, index)"
                                                :class="['img-fluid', 'swiper-slide-modal', { 'swiper-slide-modal--letterbox': shouldLetterboxImage(image, index) }]" />
                                        </swiper-slide>
                                    </swiper>
                                </div>

                                <div class="modal-info">
                                    <div class="title-section">
                                        <h2>{{ selectedItem.name }}</h2>
                                        <div class="contact-icons">
                                            <a :href="`https://wa.me/${selectedItem.whatsapp}`" target="_blank"
                                                class="contact-icon whatsapp"
                                                @click.stop.prevent="handleWhatsAppClick(selectedItem, selectedItem.whatsapp, 'primary')">
                                                <i class="fab fa-whatsapp"></i>
                                            </a>
                                            <a v-if="selectedItem.whatsapp_2"
                                                :href="`https://wa.me/${selectedItem.whatsapp_2}`" target="_blank"
                                                class="contact-icon whatsapp"
                                                @click.stop.prevent="handleWhatsAppClick(selectedItem, selectedItem.whatsapp_2, 'secondary')">
                                                <i class="fab fa-whatsapp">
                                                    <small
                                                        style="position: relative; font-size: 8px; margin-left: 2px;">2</small>
                                                </i>
                                            </a>
                                            <a v-if="selectedItem.contact_link" :href="selectedItem.contact_link"
                                                target="_blank" class="contact-icon website"
                                                :title="$t('more_info')"
                                                @click.stop.prevent="handleInfoLinkClick(selectedItem)">
                                                <i class="fas fa-external-link-alt"></i>
                                            </a>
                                            <button type="button" class="contact-icon share" :title="$t('share')"
                                                @click.stop.prevent="handleShareClick(selectedItem, 'modal')">
                                                <i class="fas fa-share-alt"></i>
                                            </button>
                                        </div>
                                        <div v-if="shareFeedback" class="share-feedback">
                                            {{ shareFeedback }}
                                        </div>
                                    </div>
                                    <p class="category">
                                        {{ selectedItem.category?.name || "" }}
                                        <span
                                            v-if="selectedItem.category?.name && selectedItem.sub_category?.name">-</span>
                                        {{ selectedItem.sub_category?.name || "" }}
                                    </p>
                                    <p v-if="hasDiscount(selectedItem.discount_percentage)" class="discount-line">
                                        <strong>{{ $t("discount_percentage") }}:</strong>
                                        <span class="discount-pill">
                                            {{ formatDiscount(selectedItem.discount_percentage) }}
                                        </span>
                                    </p>
                                    <p v-if="allyDiscountObservation(selectedItem)" class="discount-observation">
                                        <strong>{{ $t("ally_modal.discount_observation") }}:</strong>
                                        {{ allyDiscountObservation(selectedItem) }}
                                    </p>
                                    <div class="benefit-actions">
                                        <button type="button" class="benefit-button"
                                            :disabled="isBenefitDisabled(selectedItem)"
                                            :title="benefitTooltip(selectedItem)"
                                            @click.stop.prevent="handleBenefitClick(selectedItem)">
                                            {{ benefitLabel(selectedItem) }}
                                        </button>
                                    </div>
                                    <div v-if="benefitInfo" class="benefit-feedback">
                                        {{ benefitInfo }}
                                    </div>
                                    <transition name="fade">
                                        <BenefitForm v-if="showBenefitForm" :ally="selectedItem"
                                            :prefill-user="prefillUser" @submitted="handleBenefitSubmitted"
                                            @close="handleBenefitClose" @auth-required="handleAuthRequired" />
                                    </transition>
                                    <p class="location mt-3">
                                        <strong>{{ $t("location") }}:</strong>
                                        {{ selectedItem.state?.name }} - {{ selectedItem.city?.name }}
                                        <a :href="`https://www.google.com/maps?q=${selectedItem.latitude},${selectedItem.longitude}`"
                                            target="_blank" class="maps-link"
                                            @click.stop.prevent="handleMapClick(selectedItem)">
                                            <i class="fas fa-map-marker-alt"></i>
                                        </a>
                                    </p>
                                    <p v-if="agreementValidityText(selectedItem)" class="agreement-validity">
                                        <strong>{{ $t("ally_modal.agreement_validity") }}:</strong>
                                        {{ agreementValidityText(selectedItem) }}
                                    </p>
                                    <p v-if="selectedItem.phone" class="phone">
                                        <strong>{{ $t("phone") }}:</strong> {{ selectedItem.phone }}
                                    </p>
                                    <div v-if="allyHasDescription(selectedItem)" class="description mt-3">
                                        <strong class="d-block">{{ $t("description") }}:</strong>
                                        <div class="description-content" v-html="allyDescriptionHtml(selectedItem)"></div>
                                    </div>
                                </div>



                                <hr />
                                <Comments :key="selectedItem.id" :model-id="selectedItem.id" :model-type="'ally'" />
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </section>
</template>

<script>
import { Swiper, SwiperSlide } from "swiper/vue";
import SwiperCore, { Navigation, Pagination, Autoplay } from "swiper";
import Comments from "@/components/comment/comment.vue";
import BenefitForm from "@/components/alliances/BenefitForm.vue";
import { isAllyInForce, getAllyValidity } from "@/composables/useAllies";
import { trackEvent } from "@/composables/useMetrics";

import "swiper/swiper-bundle.css";
SwiperCore.use([Navigation, Pagination, Autoplay]);

const SUBJECT_TYPE = "ally";
const PAGE_CONTEXT = "home_allies";
const GLOBAL_MODAL_EVENT = "selah-home-modal-state";
const MODAL_SOURCE = "interest-allies";
const TARGET_ASPECT_RATIO = 16 / 9;
const ASPECT_TOLERANCE = 0.02;

export default {
    name: "InterestAllies",
    components: { Swiper, SwiperSlide, Comments, BenefitForm },
    props: {
        items: {
            type: Array,
            required: true,
        },
    },
    computed: {
        shouldLoop() {
            return Array.isArray(this.items) && this.items.length > 1;
        },
        effectiveSlidesPerGroup() {
            if (!Array.isArray(this.items) || this.items.length === 0) {
                return this.defaultSlidesPerGroup;
            }
            if (this.items.length < this.defaultSlidesPerGroup) {
                return 1;
            }
            return this.defaultSlidesPerGroup;
        },
    },
    data() {
        return {
            selectedItem: null,
            showModal: false,
            showBenefitForm: false,
            hoverText: "",
            shareFeedback: "",
            shareFeedbackTimeout: null,
            prefillUser: null,
            modalImageAspectMap: {},
            isAuth: !!localStorage.getItem("token"),
            benefitInfo: "",
            benefitInfoTimeout: null,
            metricsLocks: {
                image: Object.create(null),
                info: Object.create(null),
                whatsapp: Object.create(null),
                map: Object.create(null),
                view: Object.create(null),
                share: Object.create(null),
            },
            intersectionObserver: null,
            sliderInteractionBackup: null,
            mainSwiper: null,
            externalModalSources: Object.create(null),
            externalModalActive: false,
            sliderBreakpoints: {
                320: { slidesPerView: 1, spaceBetween: 10, slidesPerGroup: 1 },
                576: { slidesPerView: 2, spaceBetween: 10, slidesPerGroup: 2 },
                768: { slidesPerView: 3, spaceBetween: 15, slidesPerGroup: 3 },
                1024: { slidesPerView: 4, spaceBetween: 20, slidesPerGroup: 4 },
            },
            defaultSlidesPerView: 4,
            defaultSlidesPerGroup: 4,
            defaultSpaceBetween: 20,
            swiperFocusCleanup: null,
        };
    },
    watch: {
        items() {
            this.setupVisibilityObserver();
            this.refreshMainSwiperLoop();
            this.$nextTick(() => {
                this.installSwiperFocusGuard();
            });
        },
        showModal(isOpen) {
            this.broadcastGlobalModalState(isOpen);
            this.applySliderLockState(isOpen);
            if (!isOpen) {
                this.showBenefitForm = false;
                this.setBenefitInfo("");
            }
        },
    },
    mounted() {
        this.setupVisibilityObserver();
        this.loadPrefillUser();
        if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
            window.addEventListener(GLOBAL_MODAL_EVENT, this.handleExternalModalState);
        }
    },
    beforeUnmount() {
        this.toggleMainSliderState(false);
        this.teardownVisibilityObserver();
        this.clearShareFeedback();
        this.mainSwiper = null;
        if (this.showModal) {
            this.broadcastGlobalModalState(false);
        }
        if (this.swiperFocusCleanup) {
            this.swiperFocusCleanup();
            this.swiperFocusCleanup = null;
        }
        if (typeof window !== "undefined" && typeof window.removeEventListener === "function") {
            window.removeEventListener(GLOBAL_MODAL_EVENT, this.handleExternalModalState);
        }
        if (this.benefitInfoTimeout) {
            clearTimeout(this.benefitInfoTimeout);
            this.benefitInfoTimeout = null;
        }
    },
    methods: {
        loadPrefillUser() {
            try {
                const stored = localStorage.getItem("user");
                if (!stored) {
                    this.prefillUser = null;
                    this.isAuth = !!localStorage.getItem("token");
                    return;
                }
                this.prefillUser = JSON.parse(stored);
                this.isAuth = !!localStorage.getItem("token");
            } catch (error) {
                console.warn("Failed to load stored user profile", error);
                this.prefillUser = null;
                this.isAuth = !!localStorage.getItem("token");
            }
        },
        async ensureAllyDetail() {
            // Legacy modal already receives the data it needs, so no extra request is required here.
        },
        handleAuthRequired() {
            this.isAuth = !!localStorage.getItem("token");
            this.loadPrefillUser();
            this.showBenefitForm = false;
            this.setBenefitInfo("");
        },
        setBenefitInfo(message) {
            if (this.benefitInfoTimeout) {
                clearTimeout(this.benefitInfoTimeout);
                this.benefitInfoTimeout = null;
            }
            this.benefitInfo = message || "";
            if (message && typeof window !== "undefined") {
                this.benefitInfoTimeout = window.setTimeout(() => {
                    this.benefitInfo = "";
                    this.benefitInfoTimeout = null;
                }, 4000);
            }
        },
        installSwiperFocusGuard() {
            const swiper = this.mainSwiper;
            if (!swiper || !swiper.el) {
                return;
            }
            if (this.swiperFocusCleanup) {
                this.swiperFocusCleanup();
                this.swiperFocusCleanup = null;
            }
            const guard = () => {
                this.$nextTick(() => {
                    if (!swiper.el) {
                        return;
                    }
                    swiper.el.removeAttribute("tabindex");
                    const activeEl = typeof document !== "undefined" ? document.activeElement : null;
                    if (activeEl && swiper.el.contains(activeEl) && typeof activeEl.blur === "function") {
                        activeEl.blur();
                    }
                    const slidesSelector = ".swiper-slide, .swiper-slide-duplicate";
                    swiper.el.querySelectorAll(slidesSelector).forEach((node) => {
                        node.removeAttribute("tabindex");
                    });
                    swiper.el.querySelectorAll(".swiper-pagination-bullet").forEach((node) => {
                        node.setAttribute("tabindex", "-1");
                    });
                    swiper.el.querySelectorAll(".swiper-button-prev, .swiper-button-next").forEach((node) => {
                        node.setAttribute("tabindex", "-1");
                    });
                });
            };
            const events = ["afterInit", "slideChange", "slideChangeTransitionEnd", "loopFix", "autoplayStart"];
            events.forEach((eventName) => swiper.on(eventName, guard));
            guard();
            this.swiperFocusCleanup = () => {
                events.forEach((eventName) => swiper.off(eventName, guard));
            };
        },
        broadcastGlobalModalState(isOpen) {
            if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") {
                return;
            }
            const detail = { source: MODAL_SOURCE, isOpen: Boolean(isOpen) };
            if (typeof CustomEvent === "function") {
                window.dispatchEvent(new CustomEvent(GLOBAL_MODAL_EVENT, { detail }));
                return;
            }
            if (typeof document !== "undefined" && typeof document.createEvent === "function") {
                const event = document.createEvent("CustomEvent");
                event.initCustomEvent(GLOBAL_MODAL_EVENT, false, false, detail);
                window.dispatchEvent(event);
            }
        },
        handleExternalModalState(event) {
            if (!event || !event.detail) {
                return;
            }
            const { source, isOpen } = event.detail;
            if (!source || source === MODAL_SOURCE) {
                return;
            }
            if (isOpen) {
                this.externalModalSources[source] = true;
            } else {
                delete this.externalModalSources[source];
            }
            this.externalModalActive = Object.keys(this.externalModalSources).length > 0;
            this.applySliderLockState();
        },
        applySliderLockState(isOpenOverride = null) {
            const shouldLock =
                typeof isOpenOverride === "boolean"
                    ? isOpenOverride || this.externalModalActive
                    : this.isModalLocked();
            this.toggleMainSliderState(shouldLock);
            this.$nextTick(() => {
                this.toggleMainSliderState(shouldLock);
            });
        },
        isModalLocked() {
            return this.showModal || this.externalModalActive;
        },
        agreementValidityText(ally) {
            if (!ally) {
                return "";
            }
            const { from, to } = getAllyValidity(ally);
            if (!from && !to) {
                const fallback = ally.validity_of_agreement || ally.agreement_valid_to;
                if (!fallback) {
                    return "";
                }
                const date = new Date(fallback);
                if (Number.isNaN(date.getTime())) {
                    return fallback;
                }
                return new Intl.DateTimeFormat(undefined, {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                }).format(date);
            }
            const formatter = new Intl.DateTimeFormat(undefined, {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
            const formattedFrom = from ? formatter.format(from) : null;
            const formattedTo = to ? formatter.format(to) : null;
            if (formattedFrom && formattedTo) {
                return this.$t("ally_modal.validity_range", { from: formattedFrom, to: formattedTo });
            }
            if (formattedFrom) {
                return this.$t("ally_modal.validity_from", { from: formattedFrom });
            }
            if (formattedTo) {
                return this.$t("ally_modal.validity_to", { to: formattedTo });
            }
            return "";
        },
        benefitLabel(ally) {
            if (!ally) {
                return this.$t("ally_modal.redeem");
            }
            if (!isAllyInForce(ally)) {
                return this.$t("ally_modal.redeem_disabled");
            }
            if (!this.isAuth) {
                return this.$t("ally_modal.redeem_login");
            }
            return this.$t("ally_modal.redeem");
        },
        isBenefitDisabled() {
            return !this.isAuth;
        },
        benefitTooltip(ally) {
            if (!ally) {
                return "";
            }
            if (!isAllyInForce(ally)) {
                const { from, to } = getAllyValidity(ally);
                const formatter = new Intl.DateTimeFormat(undefined, {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                });
                const formattedFrom = from ? formatter.format(from) : null;
                const formattedTo = to ? formatter.format(to) : null;
                if (formattedFrom && formattedTo) {
                    return this.$t("ally_modal.validity_range", { from: formattedFrom, to: formattedTo });
                }
                if (formattedFrom) {
                    return this.$t("ally_modal.validity_from", { from: formattedFrom });
                }
                if (formattedTo) {
                    return this.$t("ally_modal.validity_to", { to: formattedTo });
                }
                return this.$t("ally_modal.agreement_expired");
            }
            if (!this.isAuth) {
                return this.$t("ally_modal.disabled_login");
            }
            return "";
        },
        async handleBenefitClick(ally) {
            if (!ally) {
                return;
            }
            if (!this.isAuth) {
                this.redirectToLogin();
                return;
            }
            if (this.showBenefitForm) {
                this.showBenefitForm = false;
                this.setBenefitInfo("");
                return;
            }
            if (!isAllyInForce(ally)) {
                this.setBenefitInfo(this.benefitTooltip(ally));
                return;
            }
            await this.ensureAllyDetail(ally);
            this.showBenefitForm = true;
            this.setBenefitInfo("");
        },
        handleBenefitClose() {
            this.showBenefitForm = false;
            this.setBenefitInfo("");
        },
        handleBenefitSubmitted() {
            this.showBenefitForm = true;
            this.loadPrefillUser();
            this.setBenefitInfo("");
        },
        redirectToLogin() {
            if (!this.$router) {
                return;
            }
            const redirect = this.$route?.fullPath || this.$router.currentRoute?.value?.fullPath || "/";
            this.$router.push({
                name: "login",
                query: { redirect },
            });
        },
        handleMainSwiperReady(swiper) {
            this.mainSwiper = swiper;
            this.installSwiperFocusGuard();
            if (this.isModalLocked()) {
                this.toggleMainSliderState(true);
            }
            this.refreshMainSwiperLoop();
            if (swiper?.params?.autoplay && swiper.autoplay && typeof swiper.autoplay.start === "function") {
                swiper.autoplay.start();
            }
        },
        handleCardClick(item, position) {
            this.emitInfoClick(item, position, "card");
            this.openModal(item);
        },
        handleImageClick(item, position) {
            this.emitImageClick(item, position);
            this.openModal(item);
        },
        handleWhatsAppClick(item, phone, channel) {
            if (!item?.id || !phone) {
                return;
            }
            this.openExternal(`https://wa.me/${phone}`);
            const action = channel === "secondary" ? "whatsapp_secondary" : "whatsapp_primary";
            this.emitAction(item, action, this.metricsLocks.whatsapp, `${item.id}-${channel}`, {
                location: "modal",
            });
        },
        handleInfoLinkClick(item) {
            if (!item?.id || !item?.contact_link) {
                return;
            }
            this.openExternal(item.contact_link);
            this.emitInfoClick(item, null, "external_link", { location: "modal" });
        },
        handleMapClick(item) {
            if (!item?.id || !item?.latitude || !item?.longitude) {
                return;
            }
            const url = `https://www.google.com/maps?q=${item.latitude},${item.longitude}`;
            this.openExternal(url);
            this.emitAction(item, "map_click", this.metricsLocks.map, String(item.id), {
                location: "modal",
            });
        },
        async handleShareClick(item, source = "modal") {
            if (!item?.id || typeof window === "undefined") {
                return;
            }
            const shareUrl = this.buildShareUrl(item);
            const shareMessage = this.$t("share_message_ally", { name: item.name ?? "" });
            const combinedText = shareMessage && shareUrl ? `${shareMessage}\n${shareUrl}` : shareMessage || shareUrl;
            const sharePayload = {
                title: `${item.name ?? "SELAH"} · SELAH`,
                text: shareMessage,
                url: shareUrl,
            };
            const shareKey = `${item.id}-share-${source}`;
            let method = "navigator";

            if (navigator.share) {
                try {
                    await navigator.share(sharePayload);
                    this.showShareFeedback(this.$t("share_ready"));
                    this.emitAction(item, "click_share", this.metricsLocks.share, shareKey, {
                        location: source,
                        method,
                    });
                    return;
                } catch (error) {
                    if (error?.name === "AbortError") {
                        return;
                    }
                }
            }

            method = "clipboard";
            let copied = false;

            if (navigator.clipboard && window.isSecureContext && combinedText) {
                try {
                    await navigator.clipboard.writeText(combinedText);
                    copied = true;
                } catch (error) {
                    copied = false;
                }
            }

            if (!copied && combinedText) {
                copied = this.copyTextFallback(combinedText);
                method = copied ? "fallback" : "manual";
            }

            if (copied) {
                this.showShareFeedback(this.$t("share_link_copied"));
            } else {
                this.showShareFeedback(this.$t("share_copy_prompt"));
            }

            this.emitAction(item, "click_share", this.metricsLocks.share, shareKey, {
                location: source,
                method,
            });
        },
        emitImageClick(item, position) {
            if (!item?.id) {
                return;
            }
            if (this.isModalLocked()) {
                return;
            }
            const key = `${item.id}-${position ?? 0}`;
            this.emitAction(item, "slide_info", this.metricsLocks.image, key, {
                location: "carousel",
                position: position ?? 0,
            });
        },
        emitInfoClick(item, position, trigger, extraMeta = {}) {
            if (!item?.id) {
                return;
            }
            const key = `${item.id}-${trigger ?? "info"}-${position ?? "na"}`;
            const action = trigger === "external_link" ? "external_info" : "slide_info";
            if (action === "slide_info" && this.isModalLocked()) {
                return;
            }
            this.emitAction(item, action, this.metricsLocks.info, key, {
                location: extraMeta.location ?? "card",
                trigger,
                position: position ?? undefined,
                ...extraMeta,
            });
        },
        emitAction(item, action, store, key, meta) {
            if (store[key]) {
                return;
            }
            store[key] = true;
            const payloadMeta = {
                page: PAGE_CONTEXT,
                ...meta,
            };
            trackEvent(SUBJECT_TYPE, item.id, action, payloadMeta).finally(() => {
                store[key] = false;
            });
        },
        emitSlideView(item, position) {
            if (this.isModalLocked()) {
                return;
            }
            const key = `${item.id}-view-${position ?? "na"}`;
            this.emitAction(item, "slider_view", this.metricsLocks.view, key, {
                location: "carousel",
                position: position ?? undefined,
            });
        },
        setupVisibilityObserver() {
            if (!Array.isArray(this.items) || this.items.length === 0) {
                this.teardownVisibilityObserver();
                return;
            }
            this.$nextTick(() => {
                this.teardownVisibilityObserver();
                const containerRef = this.$refs.sliderContainer;
                const rootEl = (containerRef && containerRef.$el) || containerRef || null;
                if (!rootEl) {
                    return;
                }
                const observer = new IntersectionObserver(this.handleSlideIntersect, {
                    root: null,
                    threshold: 0.6,
                });
                this.intersectionObserver = observer;
                const cards = rootEl.querySelectorAll(".theme_common_box_two");
                cards.forEach((node) => observer.observe(node));
            });
        },
        teardownVisibilityObserver() {
            if (this.intersectionObserver) {
                this.intersectionObserver.disconnect();
                this.intersectionObserver = null;
            }
        },
        handleSlideIntersect(entries) {
            if (this.isModalLocked()) {
                return;
            }
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }
                const { allyId, sliderIndex } = entry.target?.dataset || {};
                if (!allyId) {
                    return;
                }
                const ally = this.items.find((item) => String(item.id) === allyId);
                if (!ally) {
                    return;
                }
                const position = Number(sliderIndex);
                this.emitSlideView(ally, Number.isNaN(position) ? undefined : position);
            });
        },
        openModal(item) {
            this.clearShareFeedback();
            this.modalImageAspectMap = {};
            this.selectedItem = item;
            this.showModal = true;
            this.showBenefitForm = false;
            this.setBenefitInfo("");
            this.applySliderLockState();
            this.ensureAllyDetail(item);
        },
        closeModal() {
            this.clearShareFeedback();
            this.showModal = false;
            this.showBenefitForm = false;
            this.selectedItem = null;
            this.setBenefitInfo("");
            this.modalImageAspectMap = {};
            this.applySliderLockState();
        },
        openExternal(url) {
            if (typeof window !== "undefined") {
                window.open(url, "_blank", "noopener");
            }
        },
        allyDescriptionRaw(ally) {
            if (!ally) {
                return "";
            }
            const candidates = [
                ally.description,
                ally.descriptions,
                ally.detail,
                ally.details,
                ally.benefit_description,
                ally.benefitDescription,
            ];
            return candidates.find((value) => typeof value === "string" && value.trim())?.trim() || "";
        },
        allyDescriptionHtml(ally) {
            return this.allyDescriptionRaw(ally);
        },
        allyHasDescription(ally) {
            const html = this.allyDescriptionRaw(ally);
            if (!html) {
                return false;
            }
            const plain = html
                .replace(/<[^>]*>/g, " ")
                .replace(/&nbsp;/gi, " ")
                .replace(/\s+/g, " ")
                .trim();
            return plain.length > 0;
        },
        allyDiscountObservation(ally) {
            if (!ally) {
                return "";
            }
            const candidates = [
                ally.discount_observation,
                ally.discountObservation,
                ally.discount_observations,
                ally.benefit_observation,
                ally.benefitObservation,
                ally.observation,
                ally.observations,
            ];
            return candidates.find((value) => typeof value === "string" && value.trim())?.trim() || "";
        },
        buildShareUrl(item) {
            if (!item) {
                return "";
            }
            if (typeof window === "undefined" || !this.$router) {
                return "";
            }
            const origin = window.location?.origin ?? "";
            const resolved = this.$router.resolve({
                name: "ally-interest",
                query: { ally_id: item.id },
            });
            return `${origin}${resolved.href}`;
        },
        showShareFeedback(message) {
            this.shareFeedback = message;
            if (this.shareFeedbackTimeout) {
                clearTimeout(this.shareFeedbackTimeout);
            }
            if (message) {
                this.shareFeedbackTimeout = window.setTimeout(() => {
                    this.shareFeedback = "";
                    this.shareFeedbackTimeout = null;
                }, 3500);
            }
        },
        clearShareFeedback() {
            if (this.shareFeedbackTimeout) {
                clearTimeout(this.shareFeedbackTimeout);
                this.shareFeedbackTimeout = null;
            }
            this.shareFeedback = "";
        },
        copyTextFallback(text) {
            if (typeof window === "undefined" || typeof document === "undefined") {
                return false;
            }
            const textarea = document.createElement("textarea");
            textarea.value = text;
            textarea.setAttribute("readonly", "");
            textarea.style.position = "absolute";
            textarea.style.left = "-9999px";
            document.body.appendChild(textarea);
            const selection = document.getSelection();
            const selectedRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
            textarea.select();
            let copied = false;
            try {
                copied = document.execCommand("copy");
            } catch (error) {
                copied = false;
            }
            document.body.removeChild(textarea);
            if (selectedRange && selection) {
                selection.removeAllRanges();
                selection.addRange(selectedRange);
            }
            return copied;
        },
        formatNumber(number) {
            const parsed = Number(number);
            if (!Number.isFinite(parsed)) {
                return "0";
            }
            return new Intl.NumberFormat("es-ES", {
                style: "decimal",
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
                useGrouping: true,
            }).format(parsed);
        },
        hasDiscount(value) {
            const parsed = Number(value);
            return Number.isFinite(parsed) && parsed > 0;
        },
        formatDiscount(value) {
            const parsed = Number(value);
            if (!Number.isFinite(parsed)) {
                return "0%";
            }
            const formatted = new Intl.NumberFormat("es-ES", {
                style: "decimal",
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
                useGrouping: true,
            }).format(parsed);
            return `${formatted}%`;
        },
        toggleMainSliderState(isModalOpen) {
            const swiper = this.mainSwiper;
            if (!swiper) {
                return;
            }
            if (isModalOpen) {
                if (!this.sliderInteractionBackup) {
                    this.sliderInteractionBackup = {
                        allowTouchMove: swiper.allowTouchMove,
                        allowSlideNext: swiper.allowSlideNext,
                        allowSlidePrev: swiper.allowSlidePrev,
                        autoplayRunning: Boolean(swiper.autoplay?.running),
                        autoplayConfigured: Boolean(swiper.params?.autoplay),
                        navigationEnabled: Boolean(swiper.params?.navigation ?? swiper.navigation),
                    };
                }
                if (swiper.autoplay) {
                    if (typeof swiper.autoplay.stop === "function") {
                        swiper.autoplay.stop();
                    }
                    if (typeof swiper.autoplay.pause === "function") {
                        swiper.autoplay.pause();
                    }
                }
                swiper.allowTouchMove = false;
                swiper.allowSlideNext = false;
                swiper.allowSlidePrev = false;
                if (swiper.navigation && typeof swiper.navigation.disable === "function") {
                    swiper.navigation.disable();
                }
            } else {
                if (this.sliderInteractionBackup) {
                    const backup = this.sliderInteractionBackup;
                    swiper.allowTouchMove = backup.allowTouchMove;
                    swiper.allowSlideNext = backup.allowSlideNext;
                    swiper.allowSlidePrev = backup.allowSlidePrev;
                    const shouldResumeAutoplay = backup.autoplayRunning || backup.autoplayConfigured;
                    if (shouldResumeAutoplay && swiper.autoplay) {
                        if (typeof swiper.autoplay.resume === "function") {
                            swiper.autoplay.resume();
                        }
                        if (typeof swiper.autoplay.start === "function") {
                            swiper.autoplay.start();
                        }
                        this.$nextTick(() => {
                            if (swiper.autoplay && typeof swiper.autoplay.start === "function" && !swiper.autoplay.running) {
                                swiper.autoplay.start();
                            }
                        });
                    }
                    if (backup.navigationEnabled !== false && swiper.navigation && typeof swiper.navigation.enable === "function") {
                        swiper.navigation.enable();
                    }
                    this.sliderInteractionBackup = null;
                } else if (swiper.navigation && typeof swiper.navigation.enable === "function") {
                    swiper.navigation.enable();
                }
            }
            this.installSwiperFocusGuard();
        },
        refreshMainSwiperLoop() {
            this.$nextTick(() => {
                const swiper = this.mainSwiper;
                if (!swiper) {
                    return;
                }
                const shouldLoop = this.shouldLoop;
                if (swiper.params) {
                    swiper.params.loop = shouldLoop;
                    swiper.params.loopAdditionalSlides = Math.max(
                        swiper.params.loopAdditionalSlides ?? 0,
                        this.defaultSlidesPerView
                    );
                    swiper.params.loopedSlides = Array.isArray(this.items) ? this.items.length : 0;
                    swiper.params.watchOverflow = false;
                    swiper.params.slidesPerGroup = this.effectiveSlidesPerGroup;
                }
                if (typeof swiper.updateSlides === "function") {
                    swiper.updateSlides();
                }
                if (shouldLoop && swiper.params?.loop) {
                    if (typeof swiper.loopDestroy === "function") {
                        swiper.loopDestroy(true, true);
                    }
                    if (typeof swiper.loopCreate === "function") {
                        swiper.loopCreate();
                    }
                    if (typeof swiper.loopFix === "function") {
                        swiper.loopFix();
                    }
                    if (typeof swiper.slideToLoop === "function") {
                        const currentIndex =
                            typeof swiper.realIndex === "number" ? swiper.realIndex : 0;
                        swiper.slideToLoop(currentIndex, 0, false);
                    }
                } else if (!shouldLoop && typeof swiper.loopDestroy === "function") {
                    swiper.loopDestroy(true, true);
                }
                if (typeof swiper.update === "function") {
                    swiper.update();
                }
                if (swiper.params?.autoplay && swiper.autoplay && typeof swiper.autoplay.start === "function" && !swiper.autoplay.running) {
                    swiper.autoplay.start();
                }
                this.installSwiperFocusGuard();
            });
        },
        handleModalImageLoad(event, image, index) {
            const target = event?.target;
            if (!target) {
                return;
            }
            const { naturalWidth, naturalHeight } = target;
            if (!naturalWidth || !naturalHeight) {
                return;
            }
            const aspectRatio = naturalWidth / naturalHeight;
            const key = this.getModalImageKey(image, index);
            const shouldLetterbox = aspectRatio < TARGET_ASPECT_RATIO - ASPECT_TOLERANCE;
            this.modalImageAspectMap[key] = shouldLetterbox;
        },
        shouldLetterboxImage(image, index) {
            const key = this.getModalImageKey(image, index);
            return Boolean(this.modalImageAspectMap[key]);
        },
        getModalImageKey(image, index) {
            if (image?.id !== undefined && image?.id !== null) {
                return `id-${image.id}`;
            }
            if (image?.path) {
                return `path-${image.path}`;
            }
            return `index-${index}`;
        },
    },
};
</script>

<style scoped>
.theme_common_box_two {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 360px;
    margin: 0 auto;
    border-radius: 15px;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    background-color: white;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.theme_common_box_two:hover {
    transform: translateY(-5px);
    box-shadow: 0px 8px 20px rgba(0, 0, 0, 0.15);
}

.theme_two_box_img {
    position: relative;
    overflow: hidden;
}

.discount-badge {
    position: absolute;
    top: 12px;
    left: 12px;
    padding: 6px 12px;
    border-radius: 20px;
    background-color: rgba(255, 97, 85, 0.9);
    color: #fff;
    font-weight: 600;
    font-size: 14px;
    z-index: 2;
}

.theme_two_box_img img {
    width: 100%;
    height: 220px;
    object-fit: cover;
    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
    filter: brightness(0.9);
    transition: transform 0.3s ease;
}

.theme_common_box_two:hover .theme_two_box_img img {
    transform: scale(1.05);
}

.theme_two_box_content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 20px;
    text-align: center;
    flex: 1 1 auto;
    justify-content: center;
    margin-top: 0;
    min-height: 120px;
}

.theme_two_box_content h4 {
    font-size: clamp(1.1rem, 2.8vw, 1.4rem);
    font-weight: 600;
    margin-bottom: 0;
    color: #333;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.theme_two_box_content h3 {
    font-size: clamp(1rem, 2.6vw, 1.2rem);
    color: #5d69d5;
    margin-bottom: 0;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.708);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 10px;
    width: 90%;
    max-width: 600px;
    padding: 20px;
    position: relative;
    max-height: 80vh;
    overflow-y: auto;
    margin-top: 10vh;
}

.modal-header {
    display: flex;
    justify-content: flex-end;
}

.close-btn {
    background: none;
    border: none;
    font-size: 2rem;
    cursor: pointer;
}

.modal-body img {
    width: 100%;
    border-radius: 10px;
    margin-bottom: 15px;
}

.modal-info {
    margin-top: 1rem;
}

.modal-slider {
    position: relative;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    overflow: hidden;
}

.modal-slider img {
    border-radius: 0;
    margin-bottom: 0;
}

.title-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.title-section h2 {
    margin: 0;
    flex: 1;
}

.contact-icons {
    display: flex;
    gap: 15px;
    align-items: center;
    flex-shrink: 0;
}

.contact-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    text-decoration: none;
    color: white;
    font-size: 16px;
    transition: background-color 0.3s ease;
    border: none;
    cursor: pointer;
}

.contact-icon.whatsapp {
    background-color: #25d366;
}

.contact-icon.website {
    background-color: #5d69d5;
}

.contact-icon.share {
    background-color: var(--coral-red-color);
}

.contact-icon:hover {
    opacity: 0.9;
}

.maps-link {
    margin-left: 10px;
    color: #e74c3c;
    font-size: 1.2rem;
}

.hover-text {
    position: absolute;
    right: 40px;
    white-space: nowrap;
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 5px 10px;
    border-radius: 5px;
    font-size: 14px;
    opacity: 1;
    transition: opacity 0.3s ease;
}

.share-feedback {
    margin-top: 8px;
    font-size: 12px;
    color: #555;
    text-align: right;
}

.modal-info p {
    font-size: 16px;
    margin-bottom: 10px;
    color: #555;
}

.discount-line {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 10px 0;
    color: #333;
}

.discount-pill {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 999px;
    background-color: rgba(255, 97, 85, 0.12);
    color: #ff6155;
    font-weight: 600;
}

.benefit-feedback {
    margin-top: 8px;
    color: #b91c1c;
    font-size: 0.9rem;
    font-weight: 600;
}

.agreement-validity {
    color: #374151;
}

.benefit-actions {
    margin: 16px 0;
    display: flex;
    justify-content: flex-start;
}

.benefit-button {
    background: linear-gradient(135deg, #f97316, #ec4899);
    color: #ffffff;
    border: none;
    border-radius: 999px;
    font-weight: 600;
    padding: 10px 24px;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.benefit-button:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
}

.benefit-button:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(236, 72, 153, 0.3);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.modal-info .category {
    font-weight: bold;
    color: #5d69d5;
}

.modal-info .location {
    color: #333;
}

.modal-info .investment {
    color: #333;
}

.modal-info .phone {
    color: #333;
}

.fab.fa-whatsapp,
.fas.fa-external-link-alt {
    color: white;
}

.fas.fa-map-marker-alt {
    font-size: 1.5em;
    color: #ff0000;
}

:deep(.swiper-button-prev),
:deep(.swiper-button-next) {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.25s ease;
}

:deep(.swiper:hover .swiper-button-prev),
:deep(.swiper:hover .swiper-button-next),
:deep(.swiper-button-prev:focus),
:deep(.swiper-button-next:focus) {
    opacity: 1;
    pointer-events: auto;
}

@media (hover: none) and (pointer: coarse) {

    :deep(.swiper-button-prev),
    :deep(.swiper-button-next) {
        opacity: 1;
        pointer-events: auto;
    }
}

.swiper-modal {
    width: 100%;
    border-radius: 0;
}

.swiper-slide-modal {
    display: block;
    width: 100;
    height: 100%;
    object-fit: cover;
    object-position: center;
}

.swiper-slide-modal--letterbox {
    object-fit: contain;
    background-color: #000;
}

:deep(.interest-allies-swiper .swiper-wrapper) {
    height: auto !important;
    min-height: 0;
    align-items: stretch;
}

:deep(.interest-allies-swiper .swiper-slide) {
    height: auto;
    display: flex;
}

:deep(.interest-allies-swiper .swiper-slide > *) {
    height: 100%;
    width: 100%;
}

:deep(.swiper-modal .swiper-wrapper) {
    height: 35vh;
}
</style>
